if (!customElements.get('mobile-sticky-atc')) {
  customElements.define('mobile-sticky-atc', class extends HTMLElement {
    connectedCallback() {
      if (!window.IntersectionObserver) return;
      this.product = this.closest('product-info');
      if (!this.product) return;
      this.button = this.querySelector('[data-sticky-submit]');
      this.label = this.querySelector('[data-sticky-label]');
      this.price = this.querySelector('[data-sticky-price]');
      this.error = this.querySelector('[data-sticky-error]');
      this.mobile = matchMedia('(max-width: 749px)');
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
      };
      this.click = () => {
        const original = this.original;
        if (!original || original.disabled || original.getAttribute('aria-disabled') === 'true') return;
        this.dispatchEvent(new CustomEvent('lucidius:sticky-atc', {bubbles:true, detail:{product_id:this.product.dataset.productId}}));
        original.form?.requestSubmit(original);
        this.schedule();
      };
      this.button.addEventListener('click', this.click);
      this.mobile.addEventListener('change', this.schedule);
      this.product.addEventListener('lucidius:volume-price', this.schedule);
      this.onScroll = () => { if (this.mobile.matches) this.schedule(); };
      window.addEventListener('scroll', this.onScroll, {passive: true});
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (event.data?.sectionId === this.dataset.section) this.schedule();
        });
      }
      this.intersection = new IntersectionObserver(entries => {
        this.pastButton = !entries[0].isIntersecting && entries[0].boundingClientRect.bottom <= 0;
        this.schedule();
      });
      this.productObserver = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.productObserver.observe(this.product, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden']});
      this.overlayObserver = new MutationObserver(this.schedule);
      this.overlayObserver.observe(document.body, {attributes: true, attributeFilter: ['class']});
      this.overlayNodes = new WeakSet();
      this.bindOverlays = () => {
        document.querySelectorAll('cart-drawer, dialog, modal-dialog, quick-add-modal, product-modal, [aria-modal="true"]').forEach(node => {
          if (this.overlayNodes.has(node)) return;
          this.overlayNodes.add(node);
          this.overlayObserver.observe(node, {attributes: true, attributeFilter: ['class', 'open', 'hidden', 'aria-hidden']});
        });
        this.schedule();
      };
      // Observe insertion only: overlay attributes are watched on their hosts.
      this.insertionObserver = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && !this.contains(node)))) this.bindOverlays();
      });
      this.insertionObserver.observe(document.body, {childList: true});
      this.shadowObservers = [];
      this.revoqHosts = new Set();
      this.discoverRevoq = () => {
        document.querySelectorAll('.widerruf-popup-source--embed').forEach(host => {
          if (!host.shadowRoot || this.revoqHosts.has(host)) return;
          this.revoqHosts.add(host);
          if (!host.shadowRoot.querySelector('[data-lucidius-sticky-offset]')) {
            const style = document.createElement('style');
            style.setAttribute('data-lucidius-sticky-offset', '');
            style.textContent = ':host([data-lucidius-sticky-atc]) .widerruf-trigger-button--floating{bottom:calc(var(--lucidius-sticky-atc-height,90px) + 12px)!important}';
            host.shadowRoot.appendChild(style);
          }
          const observer = new MutationObserver(this.schedule);
          observer.observe(host.shadowRoot, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden', 'open', 'aria-hidden']});
          this.shadowObservers.push(observer);
        });
        this.schedule();
      };
      this.discoveryTimer = setInterval(this.discoverRevoq, 500);
      this.discoveryTimeout = setTimeout(() => clearInterval(this.discoveryTimer), 10000);
      this.resize = new ResizeObserver(this.schedule);
      this.resize.observe(this);
      this.bindOverlays();
      this.discoverRevoq();
      this.sync();
    }

    sync() {
      const original = this.product.querySelector('[id^="ProductSubmitButton-"]');
      if (original !== this.original) {
        if (this.original) this.intersection.unobserve(this.original);
        this.original = original;
        this.pastButton = false;
        if (original) this.intersection.observe(original);
      }
      if (!original) { this.setVisible(false); return; }
      // A jump can move the button from above to below the viewport without
      // changing its intersection ratio. Refresh geometry before DOM writes.
      this.pastButton = original.getBoundingClientRect().bottom <= 0;
      const busy = original.classList.contains('loading');
      this.button.disabled = original.disabled || original.getAttribute('aria-disabled') === 'true';
      this.button.classList.toggle('loading', busy);
      this.button.setAttribute('aria-busy', String(busy));
      this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
      const text = original.querySelector('span')?.textContent.trim() || '';
      if (this.label.textContent !== text) this.label.textContent = text;
      const price = this.product.querySelector('[id^="price-"]');
      const nativePrice = price?.querySelector('.price');
      const currentGroup = nativePrice?.querySelector(nativePrice.classList.contains('price--on-sale') ? '.price__sale' : '.price__regular');
      // Volume pricing can prepend a struck compare-at item to the regular group.
      const priceItems = (currentGroup || price)?.querySelectorAll('.price-item') || [];
      const currentItem = [...priceItems].find(item =>
        !item.closest('s, del, .unit-price, [hidden], .hidden') && (currentGroup || !item.closest('.price__sale'))
      );
      const hiddenPrice = !price || price.classList.contains('hidden') || nativePrice?.classList.contains('price--unavailable');
      const currentText = hiddenPrice ? '' : currentItem?.textContent.trim() || '';
      const unitItem = nativePrice?.querySelector('.unit-price .price-item');
      const unitText = !!currentText && unitItem && !unitItem.closest('[hidden], .hidden') ? unitItem.textContent.trim() : '';
      const priceText = this.product.dataset.volumeUnitPrice || [currentText, unitText].filter(Boolean).join(' · ');
      if (this.price.textContent !== priceText) this.price.textContent = priceText;
      const sourceError = original.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const errorText = sourceError && !sourceError.hidden ? sourceError.querySelector('.product-form__error-message')?.textContent.trim() || '' : '';
      if (this.error.textContent !== errorText) this.error.textContent = errorText;
      this.error.hidden = !errorText;
      const locked = [...document.body.classList].some(name => name.startsWith('overflow-hidden'));
      const overlay = document.querySelector('cart-drawer.active, dialog[open], modal-dialog[open], quick-add-modal[open], product-modal[open], [aria-modal="true"]:not([hidden])[aria-hidden="false"]');
      const revoqModal = [...this.revoqHosts].some(host => host.shadowRoot.querySelector('.widerruf-popup-modal:not([hidden])'));
      this.setVisible(this.mobile.matches && this.pastButton && !locked && !overlay && !revoqModal);
    }

    setVisible(visible) {
      this.hidden = !visible;
      document.body.classList.toggle('lucidius-sticky-atc-visible', visible);
      if (visible) {
        const height = `${Math.ceil(this.getBoundingClientRect().height)}px`;
        if (document.body.style.getPropertyValue('--lucidius-sticky-atc-height') !== height) document.body.style.setProperty('--lucidius-sticky-atc-height', height);
      }
      this.revoqHosts.forEach(host => host.toggleAttribute('data-lucidius-sticky-atc', visible));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearInterval(this.discoveryTimer);
      clearTimeout(this.discoveryTimeout);
      this.unsubscribeVariant?.();
      this.intersection?.disconnect();
      this.productObserver?.disconnect();
      this.overlayObserver?.disconnect();
      this.insertionObserver?.disconnect();
      this.resize?.disconnect();
      this.shadowObservers?.forEach(observer => observer.disconnect());
      this.mobile?.removeEventListener('change', this.schedule);
      this.product?.removeEventListener('lucidius:volume-price', this.schedule);
      window.removeEventListener('scroll', this.onScroll);
      this.button?.removeEventListener('click', this.click);
      this.revoqHosts?.forEach(host => host.removeAttribute('data-lucidius-sticky-atc'));
      document.body.classList.remove('lucidius-sticky-atc-visible');
      document.body.style.removeProperty('--lucidius-sticky-atc-height');
    }
  });
}
